"use client";
import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { OverlapSchemes } from "@/type/overlapType";
import { filterSchemes } from "@/utils/overlapPageFunctions";
import { ROOT_URL } from "@/utils/gloableVariables";

const PAGE_SIZE = 100;

export default function SelectOverlap({ schemes }: { schemes: OverlapSchemes[] }) { 
  const [dropdownA, setDropdownA] = useState(false);
  const [selectItemA, setSelectItemA] = useState<OverlapSchemes | null>(null);
  const [searchA, setSearchA] = useState("");

  const [dropdownB, setDropdownB] = useState(false);
  const [selectItemB, setSelectItemB] = useState<OverlapSchemes | null>(null);
  const [searchB, setSearchB] = useState("");

  const schemeDropdownA = useRef<HTMLButtonElement | null>(null);
  const searchInputRefA = useRef<HTMLInputElement | null>(null);
  const schemeDropdownB = useRef<HTMLButtonElement | null>(null);
  const searchInputRefB = useRef<HTMLInputElement | null>(null);

  const [visibleCountA, setVisibleCountA] = useState(PAGE_SIZE);
  const [visibleCountB, setVisibleCountB] = useState(PAGE_SIZE);

  const [newUrl, setNewUrl] = useState("");

  const handleSelectA = (scheme: OverlapSchemes): void => {
    setDropdownA(false);
    setSelectItemA(scheme);
  };

  const handleSelectB = (scheme: OverlapSchemes): void => {
    setDropdownB(false);
    setSelectItemB(scheme);
  };

  const filteredSchemesA = filterSchemes(schemes, searchA, selectItemB?.id).slice(0, visibleCountA);

  const filteredSchemesB = filterSchemes(schemes, searchB, selectItemA?.id).slice(0, visibleCountB);

  useEffect(() => {
    setVisibleCountA(PAGE_SIZE);
  }, [searchA]);

  useEffect(() => {
    setVisibleCountB(PAGE_SIZE);
  }, [searchB]);

  useEffect(() => {
    if (!selectItemA || !selectItemB) return;
    const url = selectItemA.url + "-with-" + selectItemB.url;

    setNewUrl(`portfolio-overlap/${url}`);
  }, [selectItemA, selectItemB]);

  return (
    <>
      {/* Select the Fund */}
      <div className="grid md:grid-cols-2 gap-4.5 md:gap-8 xl:w-[85%] mx-auto mb-8">
        <div className="space-y-2 relative">
          <button ref={schemeDropdownA} onClick={() => setDropdownA((p) => !p)} aria-label="Open Dropdown to Select Scheme A" className="w-full flex items-center justify-start gap-2.5 rounded-full border border-overlap-blue text-overlap-blue bg-white py-2.5 px-5 md:py-3.5 shadow-[0px_0px_18.5px_0px_#00000066] transition duration-300 hover:bg-[#e9f1ff]">
            <i className="w-3 h-3 shrink-0 rounded-full bg-overlap-blue" />
            <span className="sm:text-lg sm:leading-6.5 lg:text-xl lg:leading-7 leading-1.3 font-medium text-left line-clamp-1">{selectItemA?.scheme_name ?? "Select Fund A"}</span>
            <Icon.dropdownDownArrow className={`shrink-0 w-4 h-2.5 ml-auto transition duration-300 ${dropdownA ? "rotate-180" : ""}`} />
          </button>

          <CustomDropdown isOpen={dropdownA} onClose={() => setDropdownA(false)} excludeRefs={[schemeDropdownA]} useHeight className="z-30 p-2 pb-0 w-full border mt-1 rounded-2xl shadow-lg overflow-hidden">
            <label className="group block sticky top-0 px-4 py-2 rounded-xl bg-transparent transition border border-[#b9b9b9] placeholder-white/40 hover:border-black outline-hidden">
              <Icon.searchIcon className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition text-primaryBlack group-hover:text-black" />
              <input ref={searchInputRefA} type="text" placeholder="Search Funds..." value={searchA} onChange={(e) => setSearchA(e.target.value)} className="bg-transparent px-6 transition text-primaryBlack group-hover:text-black" />
            </label>

            <div className="max-h-64 overflow-y-auto mt-1 scrollbar-md space-y-1">
              {filteredSchemesA.map((item) => (
                <button key={item.scheme_name} onClick={() => handleSelectA(item)} className="w-full flex items-center gap-2 py-1.5 px-2.5 sm:py-2 sm:px-3.5 rounded-lg text-sm sm:text-base text-left transition hover:bg-primaryBlue/10 animate-fadeIn">
                  <Image src={`${ROOT_URL}img/AMCsLogo/${item.amc_code}.png`} alt={item.scheme_name} width={40} height={40} className="w-9 h-9 object-contain shrink-0 rounded-full" />
                  <span>{item.scheme_name}</span>
                </button>
              ))}

              {filteredSchemesA.length === 0 && <div className="py-4 text-center text-sm text-[#666666] italic">No equity fund found</div>}

              {filteredSchemesA.length < filterSchemes(schemes, searchA, selectItemB?.id).length && (
                <button onClick={() => setVisibleCountA((p) => p + PAGE_SIZE)} className="w-full text-sm sm:text-base font-semibold text-center py-3 text-primaryBlue hover:underline">
                  Load more
                </button>
              )}
            </div>
          </CustomDropdown>

          <div className="flex items-center gap-2">
            {/* <span className="inline-block w-fit border border-white/80 rounded-full px-3.5 md:px-5 py-1.5 md:py-2 text-2xs md:text-sm leading-1.3 text-white capitalize">{selectItemA?.category_type ?? "Category:"}</span> */}
            <span className="inline-block w-fit border border-white/80 rounded-full px-3.5 md:px-5 py-1.5 md:py-2 text-2xs md:text-sm md:leading-5 leading-1.3 text-white"> Category: {selectItemA?.scheme_category}</span>
          </div>
        </div>

        <div className="space-y-2 relative">
          <button ref={schemeDropdownB} onClick={() => setDropdownB((p) => !p)} aria-label="Open Dropdown to Select Scheme B" className="w-full flex items-center justify-start gap-2.5 rounded-full border border-overlap-yellow text-overlap-yellow bg-white py-2.5 px-5 md:py-3.5 shadow-[0px_0px_18.5px_0px_#00000066] transition duration-300 hover:bg-[#fffbf3]">
            <i className="w-3 h-3 shrink-0 rounded-full bg-overlap-yellow" />
            <span className="sm:text-lg sm:leading-6.5 lg:text-xl lg:leading-7 leading-1.3 font-medium text-left line-clamp-1">{selectItemB?.scheme_name ?? "Select Fund B"}</span>
            <Icon.dropdownDownArrow className={`shrink-0 w-4 h-2.5 ml-auto transition duration-300 ${dropdownB ? "rotate-180" : ""}`} />
          </button>

          <CustomDropdown isOpen={dropdownB} onClose={() => setDropdownB(false)} excludeRefs={[schemeDropdownB]} useHeight className="z-30 p-2 pb-0 w-full border mt-1 rounded-2xl shadow-lg overflow-hidden">
            <label className="group block sticky top-0 px-4 py-2 rounded-xl bg-transparent transition border border-[#b9b9b9] placeholder-white/40 hover:border-black outline-hidden">
              <Icon.searchIcon className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition text-primaryBlack group-hover:text-black" />
              <input ref={searchInputRefB} type="text" placeholder="Search Funds..." value={searchB} onChange={(e) => setSearchB(e.target.value)} className="bg-transparent px-6 transition text-primaryBlack group-hover:text-black" />
            </label>

            <div className="max-h-64 overflow-y-auto mt-1 scrollbar-md space-y-1">
              {filteredSchemesB.map((item) => (
                <button key={item.id} onClick={() => handleSelectB(item)} className="w-full flex items-center gap-2 py-1.5 px-2.5 sm:py-2 sm:px-3.5 rounded-lg text-left transition hover:bg-primaryBlue/10 animate-fadeIn">
                  <Image src={`${ROOT_URL}img/AMCsLogo/${item.amc_code}.png`} alt={item.scheme_name} width={40} height={40} className="w-9 h-9 object-contain shrink-0 rounded-full" />
                  <span>{item.scheme_name}</span>
                </button>
              ))}

              {filteredSchemesB.length === 0 && <div className="py-4 text-center text-sm text-[#666666] italic">No equity fund found</div>}

              {filteredSchemesB.length < filterSchemes(schemes, searchB, selectItemA?.id).length && (
                <button onClick={() => setVisibleCountB((p) => p + PAGE_SIZE)} className="w-full text-sm sm:text-base font-semibold text-center py-3 text-primaryBlue hover:underline">
                  Load more
                </button>
              )}
            </div>
          </CustomDropdown>

          <div className="flex items-center gap-2">
            {/* <span className="inline-block w-fit border border-white/80 rounded-full px-3.5 md:px-5 py-1.5 md:py-2 text-2xs md:text-sm leading-1.3 text-white capitalize">{selectItemB?.category_type ?? "Category:"}</span> */}
            <span className="inline-block w-fit border border-white/80 rounded-full px-3.5 md:px-5 py-1.5 md:py-2 text-2xs md:text-sm md:leading-5 leading-1.3 text-white"> Category: {selectItemB?.scheme_category}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-3">
        {!selectItemA || !selectItemB ? (
          <button disabled={true} title="Please Select Both Fields to Continure" className="w-full block max-w-300 border-2 border-white rounded-xl sm:rounded-2xl bg-primaryGreen text-white text-sm xs:text-base xs:leading-6 sm:text-xl sm:leading-7 leading-1.5 font-medium py-2 sm:py-3.5 opacity-70 cursor-not-allowed">
            Check Overlap
          </button>
        ) : (
          <Link href={newUrl} className="w-full text-center block max-w-300 border-2 border-white rounded-xl sm:rounded-2xl bg-primaryGreen text-white text-sm xs:text-base xs:leading-6 sm:text-xl sm:leading-7 leading-1.5 font-medium py-2 sm:py-3.5 shine-button-hover">
            Check Overlap
          </Link>
        )}

        <p className="text-xs leading-1.3 text-[#555555] italic">*NOTE: Only Equity is considered in this calculation</p>
      </div>
    </>
  );
}
